import { useMemo, useState } from 'react';
import { DropdownMenuCard } from 'components/Card';
import { OrganizationTreePopover } from 'components/OrganizationTreePopover';
import SortableFieldModal from 'components/SortableFieldsModal/SortableFieldsModal';
import { useGlobalFieldOrder } from 'hooks/useGlobalFieldOrder';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import { fieldMappingSelector, fieldsSelector } from 'slices/field.slice';
import { relationTypesSelector } from 'slices/relationType.slice';
import { reservationModesSelector } from 'slices/reservationMode.slice';
import { useAppSelector } from 'slices/store';
import type { TReservationTemplate } from '@timeedit/types/lib/types';
import { Dropdown, Typography } from '@timeedit/ui-components/antd';
import './TemplateAttributes.scss';

interface TemplateAttributesProps {
  template: TReservationTemplate;
  onChange: (key: string, value: any) => void;
  isExpand?: boolean;
}
const TemplateAttributes = ({
  template,
  onChange,
  isExpand,
}: TemplateAttributesProps) => {
  const [changeOrderModalVisible, setChangeOrderModalVisible] = useState(false);
  const relationTypes = useAppSelector(relationTypesSelector);
  const fields = useAppSelector(fieldsSelector);
  const reservationModes = useAppSelector(reservationModesSelector);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);
  const {
    globalFieldOrder,
    stagedGlobalFieldOrder,
    hasLocalChanges,
    changeGlobalOrder,
    saveGlobalOrder,
    reset,
  } = useGlobalFieldOrder();
  const fieldMap = useAppSelector(fieldMappingSelector);

  const relationTypeOptions = useMemo(
    () =>
      relationTypes.map((type) => ({
        key: String(type.id),
        label: type.name,
        url: `/${customerSignature}/configuration/relation-types?item1=${type.id}`,
      })),
    [relationTypes, customerSignature]
  );

  const templateFields = useMemo(() => {
    return [...template.fields].sort((fieldA, fieldB) => {
      const firstFieldIndex = stagedGlobalFieldOrder?.findIndex(
        (field) => fieldA === field.id
      );
      const secondFieldIndex = stagedGlobalFieldOrder?.findIndex(
        (field) => fieldB === field.id
      );
      return firstFieldIndex - secondFieldIndex;
    });
  }, [stagedGlobalFieldOrder]);

  const fieldOptions = useMemo(
    () =>
      fields.map((field) => ({
        key: String(field.id),
        label: `${field.name} (${field.extId})`,
        url: `/${customerSignature}/configuration/fields?item1=${field.id}`,
      })),
    [fields, customerSignature]
  );

  const reservationModeOptions = useMemo(
    () =>
      reservationModes.map((reservationMode) => ({
        key: String(reservationMode.id),
        label: reservationMode.name,
        url: `/${customerSignature}/configuration/reservation-modes?item1=${reservationMode.id}`,
      })),
    [reservationModes, customerSignature]
  );

  return (
    <div className="template-attributes">
      <div className={isExpand ? 'col expand' : 'col'}>
        <DropdownMenuCard
          title="Relation types"
          options={relationTypeOptions}
          selectedKeys={(template.relationTypes || []).map((val: number) =>
            String(val)
          )}
          onChange={(selectedKeys) =>
            onChange(
              'relationTypes',
              selectedKeys.map((val) => Number(val))
            )
          }
          data-testid="relation-type"
        />
      </div>
      <div className={isExpand ? 'col expand' : 'col'}>
        <DropdownMenuCard
          title="Fields"
          extra={
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'change-order',
                    label: 'Change order',
                    onClick: () => setChangeOrderModalVisible(true),
                  },
                ],
              }}
            >
              <Typography.Link underline>More</Typography.Link>
            </Dropdown>
          }
          selectedKeys={templateFields.map(String)}
          options={fieldOptions}
          onChange={(values) =>
            onChange(
              'fields',
              values.map((val) => Number(val))
            )
          }
          data-testid="linked-field"
        />
      </div>
      <div className={isExpand ? 'col expand' : 'col'}>
        <DropdownMenuCard
          title="Reservation modes"
          selectedKeys={(template.reservationModes || []).map((val: number) =>
            String(val)
          )}
          options={reservationModeOptions}
          onChange={(values) =>
            onChange(
              'reservationModes',
              values.map((val) => Number(val))
            )
          }
          data-testid="linked-resv-mode"
        />
      </div>
      <div className={isExpand ? 'col expand' : 'col'}>
        <OrganizationTreePopover
          title="Organizations"
          tooltip="Only use org node that is under the parent node “oTemplates”, for example “Template - Scheduling"
          value={template.organizations || []}
          onChange={(val) => onChange('organizations', val)}
        />
      </div>
      <SortableFieldModal
        visible={changeOrderModalVisible}
        setVisible={setChangeOrderModalVisible}
        fields={templateFields.map((fieldId) => fieldMap[fieldId])}
        hasChanges={hasLocalChanges}
        onChangeOrder={changeGlobalOrder}
        onSave={saveGlobalOrder}
        onDiscard={reset}
      />
    </div>
  );
};

export default TemplateAttributes;
